import Constants from '../Constants.js';
import Utils from '../Utils.js';
import ActionCache from '../ActionCache.js';
const {
  encodeQ
} = Utils;

// NOTE: parentPath no longer matters anywhere except for remove functionality

const cache = new ActionCache();
const setCacheTime = cacheTime => {
  cache.ttl = cacheTime;
};
const getCacheTime = () => {
  return cache.ttl;
};
const canCache = new ActionCache();
const setCanCacheTime = cacheTime => {
  canCache.ttl = cacheTime;
};
const getCanCacheTime = () => {
  return canCache.ttl;
};

/**
 * @param {string} method http method
 * @param {string} path api route to be queried
 * @param {object} body data to be sent in the api call
 * @param {object} options options to be sent in the api call
 * @param {string} type action type to be used to maintain the api-redux status
 * @param {object} extra additional information to be incuded in redux dispatches
 * @returns {function} redux thunk function
 */
function createPromise(method, path, body, opts, type, extra = {}) {
  const {
    noCache,
    ttl,
    clearOnError,
    ...options
  } = opts || {};
  /**
   * @returns {promise} representing the result of an api call
   */
  return function action(dispatch, getState, args) {
    const override = extra.override;
    const api = override?.api || args?.[override?.apiKey] || args.api;
    const ex = {
      ...extra.extraValues,
      ...override?.extraValues
    };
    const basePath = override?.basePath || 'v1/interface/';
    const toCall = () => {
      // let people know we are loading
      const action = {
        ...ex,
        type: Constants.INTERFACE_STATUS,
        path,
        status: Constants.INTERFACE_STATUS_LOADING
      };
      if (extra.queryName) {
        action.queryName = extra.queryName;
      }
      const createStatusEvent = override?.createStatusEvent || extra.createStatusEvent;
      dispatch(createStatusEvent?.(action) || action);
      return api[method](`${basePath}${path}${encodeQ(options)}`, body);
    };
    let promise = cache.get({
      method,
      path,
      body,
      options,
      type,
      extra
    });
    if (!noCache && promise) {
      return promise;
    }
    promise = cache.set(toCall, {
      method,
      path,
      body,
      options,
      type,
      extra
    }, {
      ttl
    });
    return promise.then(data => {
      if (extra.preDispatch) {
        extra.preDispatch(data, dispatch, getState, args);
      }
      const evt = {
        ...ex,
        type,
        path,
        data,
        body,
        options
      };
      if (extra.queryName) {
        evt.queryName = extra.queryName;
      }
      const createEvent = override?.createEvent || extra.createEvent;
      dispatch(createEvent?.(evt) || evt);
      return data;
    }).catch(err => {
      const error = {
        ...ex,
        err,
        clearOnError,
        type: Constants.INTERFACE_STATUS,
        path,
        status: Constants.INTERFACE_STATUS_ERROR
      };
      if (extra.queryName) {
        error.queryName = extra.queryName;
      }
      const createErrorEvent = override?.createErrorEvent || extra.createEvent;
      dispatch(createErrorEvent?.(error) || error);
      if (err && (err.status === '401' || err.status === 401)) {
        dispatch({
          type: Constants.AUTH_LOGGED_OUT
        });
      }
      throw err;
    });
  };
}

/**
 * @param {string} path route to be queried from the api
 * @param {string} parentId id of the parent device
 * @param {string} type action type to maintain the redux state
 * @param {object} options to be included in the api call
 * @returns {function} call to createPromise
 */
function list(path, parentId, type, options, queryName, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('get', path, null, options, type, {
    extraValues: {
      parentId
    },
    queryName,
    override
  });
}
const mutateSearchEvent = evt => {
  const nEvt = {
    ...evt
  };
  if (evt.originalPath) {
    nEvt.path = nEvt.originalPath;
    delete nEvt.originalPath;
  }
  return nEvt;
};
/**
 * @param {string} path route to be queried from the api
 * @param {object} obj the search to execute
 * @param {string} parentId id of the parent device
 * @param {string} type action type to maintain the redux state
 * @param {object} options to be included in the api call
 * @returns {function} call to createPromise
 */
function search(path, obj, parentId, type, options, queryName, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('post', `${path}${override?.pathSuffix ?? '.search'}`, obj, options, type, {
    extraValues: {
      parentId,
      originalPath: path
    },
    createEvent: mutateSearchEvent,
    createErrorEvent: mutateSearchEvent,
    createStatusEvent: mutateSearchEvent,
    queryName,
    override
  });
}

/**
 * @param {string} path route to be queried from the api
 * @param {object} obj the item to be created
 * @param {string} parentId id of the parent device
 * @param {string} type action type to maintain the redux state
 * @param {object} options to be included in the api call
 * @returns {function} call to createPromise
 */
function create(path, obj, parentId, type, options, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('post', path, obj, {
    ...options,
    noCache: true
  }, type, {
    extraValues: {
      parentId
    },
    override
  });
}

/**
 * @param {string} path route to be queried from the api
 * @param {string} parentPath path to the parent device
 * @param {object} options to be included in the api call
 * @returns {function} call to createPromise
 */
function get(path, parentPath, options, queryName, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('get', path, null, options, Constants.INTERFACE_GET, {
    extraValues: {
      parentPath
    },
    queryName,
    override
  });
}

/**
 * @param {string} path route to be queried from the api
 * @param {string} parentPath path to the parent device
 * @param {object} options to be included in the api call
 * @param {string} queryName identifier to maintain different query statuses
 * @returns {function} call to createPromise
 */
function getHistory(path, parentPath, options, queryName, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('get', path, null, options, Constants.INTERFACE_GET_HISTORY, {
    extraValues: {
      parentPath,
      time: Date.now()
    },
    queryName,
    override
  });
}
function createEvent(path, parentPath, data, override) {
  return createPromise('post', path, data, null, Constants.INTERFACE_CREATE_EVENT, {
    extraValues: {
      parentPath
    }
  }, override);
}

/**
 * @param {string} path route to be queried from the api
 * @param {string} id device to be removed
 * @param {object} body to be included in the api call
 * @param {object} options to be included in the api call
 * @param {object} reducerOptions options for the remove call
 * @param {object} reducerOptions.parentKey the parent pointer in the data
 * @returns {function} call to createPromise
 */
function remove(path, id, body, options, reducerOptions, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('delete', override?.pathIncludesId ? path : `${path}/${id}`, body, {
    ...options,
    noCache: true
  }, Constants.INTERFACE_REMOVE, {
    extraValues: {
      ...reducerOptions,
      parentPath: path,
      id
    },
    override
  });
}

/**
 * @param {string} path route to be queried from the api
 * @param {object} data data to update the device with
 * @param {string} parentPath path to the parent device
 * @param {string} parentId id of the parent device
 * @param {object} options to be included in the api call
 * @returns {function} call to createPromise
 */
function update(path, data, parentPath, parentId, type, options, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('patch', path, data, {
    ...options,
    noCache: true
  }, type, {
    extraValues: {
      parentPath,
      parentId
    },
    override
  });
}

/**
 * sends an outbound message to a device
 * @param {string} path route to be queried from the api
 * @param {object} obj message to be sent to the device
 * @param {string} parentId id of the parent device
 * @param {object} options to be included in the api call
 * @returns {function} call to createPromise
 */
function sendOutboundDeviceMessage(path, obj, parentId, options, override) {
  /**
   * @returns {promise} represents the result of the api call
   */
  return createPromise('post', path, obj, options, Constants.INTERFACE_SEND_OUTBOUND_DEVICE_MESSAGE, {
    extraValues: {
      parentId,
      msg: obj
    },
    override
  });
}

/**
 * Invokes getDeviceByNetworkId on the inteface path.
 * @param {object|string} network either the networkId or network object with an id field
 * @param {string} key the name of the value field
 * @param {string} value the value to lookup
 * @returns {promise} Resolves to a device
 */
function getDeviceByNetworkAlias(path, network, key, value, options) {
  return async (dispatch, getState, {
    api
  }) => {
    const nid = typeof network === 'object' ? network.id : network;
    const eNid = encodeURIComponent(nid);
    const eKey = encodeURIComponent(key);
    const eVal = encodeURIComponent(value);
    const urlPath = `v1/interface/${path}.deviceByNetworkAlias/${eNid}/${eKey}/${eVal}`;
    try {
      const device = await api.child(urlPath).get(options);
      dispatch({
        type: Constants.GET_DEVICE_BY_NETWORK_ALIAS,
        networkId: network,
        key,
        value,
        device
      });
      return device;
    } catch (err) {
      dispatch({
        type: Constants.GET_DEVICE_BY_NETWORK_ALIAS,
        networkId: network,
        key,
        value,
        error: err
      });
      throw err;
    }
  };
}
function interfaceCan(path, resourceId, method, action, canOptions, extra) {
  return async (dispatch, getState, args) => {
    const {
      query,
      body
    } = canOptions;
    const api = extra.override?.api || args?.[extra.override?.apiKey] || args.api;
    const basePath = extra.override?.basePath || 'v1/interface/';
    const toCall = async () => {
      const r = await api?.[method]?.(`${basePath}${path}${encodeQ({
        test: true,
        ...(query || {})
      })}`, method !== 'get' ? body : null);
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId,
        action,
        allowed: r.allowed === true
      });
      return r;
    };
    const promise = cache.get({
      method,
      path,
      resourceId,
      action,
      query,
      body
    });
    if (promise) {
      return promise;
    }
    return cache.set(toCall, {
      method,
      path,
      resourceId,
      action,
      query,
      body
    });
  };
}
function canCreate(path, query, body, override) {
  return interfaceCan(path, path, 'post', 'create', {
    query,
    body
  }, {
    override
  });
}
function canAdd(path, query, body, override) {
  return interfaceCan(path, path, 'post', 'add', {
    query,
    body
  }, {
    override
  });
}
function canGet(path, query, override) {
  return interfaceCan(path, path, 'get', 'get', {
    query
  }, {
    override
  });
}
function canUpdate(path, query, body, override) {
  return interfaceCan(path, path, 'patch', 'update', {
    query,
    body
  }, {
    override
  });
}
function canDelete(path, query, body, override) {
  return interfaceCan(path, path, 'delete', 'delete', {
    query,
    body
  }, {
    override
  });
}
function canRemove(path, query, body, override) {
  return interfaceCan(path, path, 'delete', 'remove', {
    query,
    body
  }, {
    override
  });
}
function canList(path, query, override) {
  return canGet(path, query, override);
}
function canSendOutboundDeviceMessage(path, query, body, override) {
  return interfaceCan(`${path}.message`, path, 'post', 'sendOutboundDeviceMessage', {
    query,
    body
  }, {
    override
  });
}
function canGetDeviceByNetworkAlias(path, network, key, value, query, override) {
  const nid = typeof network === 'object' ? network.id : network;
  const eNid = encodeURIComponent(nid);
  const eKey = encodeURIComponent(key);
  const eVal = encodeURIComponent(value);
  const urlPath = `${path}.deviceByNetworkAlias/${eNid}/${eKey}/${eVal}`;
  return interfaceCan(urlPath, path, 'get', 'getDeviceByNetworkAlias', {
    query
  }, {
    override
  });
}
function canGetHistory(path, query, override) {
  return interfaceCan(`${path}.history`, path, 'get', 'getHistory', {
    query
  }, {
    override
  });
}
function canGetEvents(path, query, override) {
  return interfaceCan(`${path}.events`, path, 'get', 'getEvents', {
    query
  }, {
    override
  });
}
export default {
  createPromise,
  list,
  search,
  get,
  getHistory,
  remove,
  create,
  createEvent,
  update,
  sendOutboundDeviceMessage,
  getDeviceByNetworkAlias,
  getCacheTime,
  setCacheTime,
  getCanCacheTime,
  setCanCacheTime,
  cacheClearOnComplete: ActionCache.clearOnComplete,
  canCreate,
  canAdd,
  canGet,
  canUpdate,
  canDelete,
  canRemove,
  canList,
  interfaceCan,
  canSendOutboundDeviceMessage,
  canGetDeviceByNetworkAlias,
  canGetHistory,
  canGetEvents
};